import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";

/** One agency in the org tree. Shape mirrors `AgencyService.getSubAgencyTree()`. */
export interface HierarchyNode {
  id: string;
  name: string;
  parentAgencyId: string | null;
  /** 0 for the root agency, +1 per level down. */
  depth: number;
  agentCount: number;
  ownerName: string | null;
  ownerEmail: string | null;
  /**
   * The agency's own roster — who is in it, not just how many. Without this the
   * tab could show a headcount but never who, so search matched only agency and
   * owner names and there was no breakdown to open.
   * Excludes upline owners, exactly like `agentCount`, so the two agree and a
   * hidden upline owner is no more visible here than anywhere else.
   */
  agents: Array<{ userId: string; name: string; email: string; role: string }>;
  /** Premium on leads marked 'Sold & Approved' in the selected window. */
  soldPremium: number;
  /** Premium still in 'Underwriting'. */
  underwritingPremium: number;
  /** Sold + underwriting — everything in flight. Matches the dashboard's Production. */
  production: number;
  soldPolicies: number;
  underwritingPolicies: number;
  /**
   * Agents on this agency's roster with no login in 30+ days. The activity half of the
   * back-office flags — an agency can look healthy on premium while a chunk of its
   * roster has gone dark, and headcount alone never reveals that.
   */
  staleAgents: number;
  /**
   * DISTINCT people in this agency and every agency beneath it — computed server-side.
   *
   * ⚠️ NOT a sum of children. This used to be rolled up on the client as
   * `agentCount + sum(children.totalAgents)`, which double-counts anyone who belongs to
   * both a parent and a child agency (routine for owners and upline managers). On prod
   * 2026-08-13 that reported 16 agents for InsuraLife where only 8 people exist, and
   * 14 vs 6 for THE KOKA GROUP — visibly disagreeing with the Agent Count card, which
   * has always deduplicated by user id.
   */
  totalAgents: number;
  /** Same distinct treatment for the dormant-agent rollup. */
  totalStaleAgents: number;
}

/** A node with its children resolved — what the tree actually renders. */
export interface HierarchyTreeNode extends HierarchyNode {
  children: HierarchyTreeNode[];
  /** Rolled-up production (sold + underwriting) for the whole branch. */
  totalProduction: number;
  /** Rolled-up sold-only premium for the whole branch. */
  totalSold: number;
  /** How many agencies sit beneath this one. */
  descendantAgencies: number;
}

export async function getAgencyHierarchy(
  agencyId: string,
  range?: { startDate?: string; endDate?: string; period?: string },
): Promise<HierarchyNode[]> {
  const qs = new URLSearchParams();
  // The dashboard sends an explicit range ONLY for custom ranges; presets come through
  // as `period` and are resolved server-side with the same helper agency-stats uses.
  if (range?.startDate) qs.set("startDate", range.startDate);
  if (range?.endDate) qs.set("endDate", range.endDate);
  if (range?.period) qs.set("period", range.period);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";

  const res = await api.get<any>({
    url: getApiUrl(endpoint.agency.getSubAgencyTree(agencyId)) + suffix,
  });

  // ⚠️ This response is DOUBLE-wrapped: the controller returns its own
  // `{ statusCode, message, data }` envelope and the global TransformInterceptor wraps
  // that again — so the array lives at `data.data.data`. Unwrap by looking for the first
  // array rather than hard-coding the depth, so the component keeps working if the
  // controller is ever cleaned up to return the array directly.
  const payload = res.data;
  const rows =
    (Array.isArray(payload?.data?.data) && payload.data.data) ||
    (Array.isArray(payload?.data) && payload.data) ||
    (Array.isArray(payload) && payload) ||
    [];
  return rows as HierarchyNode[];
}

/**
 * Flat rows -> nested tree, with rolled-up totals.
 *
 * The API returns a flat list (one row per agency, ordered by depth) because that is
 * what a recursive CTE naturally produces and it keeps the payload small. Nesting is
 * done here rather than server-side so the same response can also drive flat views
 * (search, a table) without a second request.
 *
 * Roll-ups are computed bottom-up in one pass: a node's total is its own value plus the
 * totals of its already-computed children, so an 8-level org costs one traversal rather
 * than one per node.
 *
 * Orphans (parent missing because it was deleted or sits outside this subtree) are
 * promoted to roots instead of being dropped — silently vanishing agencies would be a
 * far worse failure than showing one at the wrong indent.
 */
export function buildHierarchyTree(rows: HierarchyNode[]): HierarchyTreeNode[] {
  const byId = new Map<string, HierarchyTreeNode>();
  for (const row of rows) {
    byId.set(row.id, {
      ...row,
      children: [],
      totalProduction: 0,
      totalSold: 0,
      descendantAgencies: 0,
      // An older backend supplies none of these; treat missing as zero rather than
      // letting NaN poison every rollup above it.
      staleAgents: row.staleAgents ?? 0,
      totalAgents: row.totalAgents ?? row.agentCount ?? 0,
      totalStaleAgents: row.totalStaleAgents ?? row.staleAgents ?? 0,
    });
  }

  const roots: HierarchyTreeNode[] = [];
  for (const node of byId.values()) {
    const parent = node.parentAgencyId ? byId.get(node.parentAgencyId) : null;
    if (parent) parent.children.push(node);
    else roots.push(node); // root, or an orphan we refuse to hide
  }

  const rollUp = (node: HierarchyTreeNode): void => {
    node.children.forEach(rollUp);
    node.totalProduction =
      node.production + node.children.reduce((n, c) => n + c.totalProduction, 0);
    node.totalSold = node.soldPremium + node.children.reduce((n, c) => n + c.totalSold, 0);
    node.descendantAgencies = node.children.reduce(
      (n, c) => n + 1 + c.descendantAgencies,
      0,
    );
  };
  roots.forEach(rollUp);

  const sortTree = (nodes: HierarchyTreeNode[]): void => {
    // Biggest orgs first — an owner scanning the tree cares about where the people are.
    nodes.sort((a, b) => b.totalAgents - a.totalAgents || a.name.localeCompare(b.name));
    nodes.forEach((n) => sortTree(n.children));
  };
  sortTree(roots);

  return roots;
}
